import type { ChangeEvent, ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, changeLanguage, isSupportedLanguage } from "src/i18n";
import { useEnabledLanguagesContext } from "src/components/providers/EnabledLanguagesProvider";
import type { Theme } from "src/theme";

type Props = {
  /** Optional visible label rendered before the dropdown (also the accessible name). */
  label?: string;
  /** Adds a data-testid to the <select> for tests. Defaults to "language-selector". */
  testId?: string;
  className?: string;
};

const selectStyle = ({ gridUnit, fontSize, colors }: Theme) => ({
  padding: `${gridUnit * 2}px ${gridUnit * 3}px`,
  fontSize,
  color: colors.textPrimary,
  // The app has no dark theme / surface token yet; "white" matches the other
  // surfaces (headers, cards). Swap for a theme surface token if one is added.
  background: "white",
  border: `2px solid ${colors.grey8}`,
  borderRadius: gridUnit * 2,
  cursor: "pointer",
  outline: "none",
  "&:focus": {
    borderColor: colors.primary
  }
});

const labelStyle = ({ gridUnit, fontSize, colors }: Theme) => ({
  fontSize,
  fontWeight: "600",
  color: colors.textPrimary,
  marginRight: gridUnit * 2
});

/**
 * Dropdown that switches the active UI language. The chosen language is applied
 * immediately and persisted via {@link changeLanguage}.
 */
const LanguageSelector = ({ label, testId = "language-selector", className }: Props): ReactNode => {
  const { t, i18n } = useTranslation();
  const enabledLanguages = useEnabledLanguagesContext();

  // Only offer released languages. If the active language is not currently in
  // the enabled set (a brief window before LanguageGate clamps it), still list
  // it so the control reflects reality and never shows a mismatched value.
  const options = useMemo(
    () =>
      enabledLanguages.some((l) => l.code === i18n.language)
        ? enabledLanguages
        : [...enabledLanguages, ...SUPPORTED_LANGUAGES.filter((l) => l.code === i18n.language)],
    [enabledLanguages, i18n.language]
  );

  const onChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const code = event.currentTarget.value;
    if (isSupportedLanguage(code)) {
      void changeLanguage(code);
    }
  };

  return (
    // Wrapping <label> gives the control an accessible name AND makes the visible
    // text click-to-focus the select via label containment.
    <label className={className} css={{ display: "inline-flex", alignItems: "center" }}>
      {label ? <span css={labelStyle}>{label}</span> : null}
      <select
        css={selectStyle}
        value={i18n.language}
        onChange={onChange}
        data-testid={testId}
        aria-label={label ?? t("common.language")}
      >
        {options.map(({ code, nativeName }) => (
          <option key={code} value={code}>
            {nativeName}
          </option>
        ))}
      </select>
    </label>
  );
};

export default LanguageSelector;
